import { useState, useEffect, useCallback } from 'react';
import { Dialog, Button, InputText } from './aip';
import type { DiarizedSegment, Speaker } from '../services/manageNewApi';
import { updateSpeakerName } from '../services/manageNewApi';

export interface DiarizeModalProps {
  /** モーダルが開いているか */
  open: boolean;
  /** 閉じる時のコールバック */
  onClose: () => void;
  /** 議事録ID */
  minutesId: number;
  /** 話者リスト */
  speakers: Speaker[];
  /** セグメントリスト */
  segments: DiarizedSegment[];
  /** 話者名更新後のコールバック */
  onSpeakerNameUpdated?: (speakerId: number, name: string) => void;
}

/**
 * 時間をフォーマット (mm:ss)
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 話者の色を取得
 */
const getSpeakerColor = (speakerId: number): string => {
  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
  ];
  return colors[speakerId % colors.length];
};

/**
 * 話者分離結果表示モーダル
 */
export const DiarizeModal: React.FC<DiarizeModalProps> = ({
  open,
  onClose,
  minutesId,
  speakers: initialSpeakers,
  segments,
  onSpeakerNameUpdated,
}) => {
  // 話者名の編集状態
  const [editingSpeakerId, setEditingSpeakerId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);
  const [speakers, setSpeakers] = useState<Speaker[]>(initialSpeakers);

  // 初期話者リストを更新
  useEffect(() => {
    setSpeakers(initialSpeakers);
  }, [initialSpeakers]);

  // 話者名の表示名を取得
  const getSpeakerDisplayName = useCallback((speakerId: number): string => {
    const speaker = speakers.find(s => s.speaker_id === speakerId);
    return speaker?.speaker_name || `話者 ${speakerId + 1}`;
  }, [speakers]);

  // 編集開始
  const handleStartEdit = (speakerId: number) => {
    const speaker = speakers.find(s => s.speaker_id === speakerId);
    setEditingSpeakerId(speakerId);
    setEditingName(speaker?.speaker_name || '');
  };

  // 編集キャンセル
  const handleCancelEdit = () => {
    setEditingSpeakerId(null);
    setEditingName('');
  };

  // 話者名を保存
  const handleSaveSpeakerName = async () => {
    if (editingSpeakerId === null) return;

    setSaving(true);
    try {
      await updateSpeakerName(minutesId, editingSpeakerId, editingName);
      
      // ローカル状態を更新
      setSpeakers(prev => 
        prev.map(s => 
          s.speaker_id === editingSpeakerId
            ? { ...s, speaker_name: editingName || null }
            : s
        )
      );

      onSpeakerNameUpdated?.(editingSpeakerId, editingName);
      setEditingSpeakerId(null);
      setEditingName('');
    } catch (error) {
      console.error('Failed to update speaker name:', error);
      alert('話者名の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="文字起こし（話者分離）"
      type="modal"
      className="diarize-modal"
      footer={
        <Button buttonType="outline" size="md" onClick={onClose}>
          閉じる
        </Button>
      }
    >
      <div className="diarize-modal__content">
        {/* 話者リスト */}
        <div className="diarize-modal__speakers">
          <div className="diarize-modal__speakers-title">話者一覧</div>
          <div className="diarize-modal__speakers-list">
            {speakers.map((speaker) => (
              <div
                key={speaker.speaker_id}
                className="diarize-modal__speaker-item"
              >
                <span
                  className="diarize-modal__speaker-badge"
                  style={{ backgroundColor: getSpeakerColor(speaker.speaker_id) }}
                >
                  {speaker.speaker_id + 1}
                </span>
                
                {editingSpeakerId === speaker.speaker_id ? (
                  <div className="diarize-modal__speaker-edit">
                    <InputText
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      placeholder="名前を入力"
                      style={{ flex: 1 }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveSpeakerName();
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      autoFocus
                    />
                    <Button
                      buttonType="solid-fill"
                      size="sm"
                      onClick={handleSaveSpeakerName}
                      disabled={saving}
                    >
                      {saving ? '...' : '保存'}
                    </Button>
                    <Button
                      buttonType="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={saving}
                    >
                      取消
                    </Button>
                  </div>
                ) : (
                  <div className="diarize-modal__speaker-display">
                    <span className="diarize-modal__speaker-name">
                      {speaker.speaker_name || `話者 ${speaker.speaker_id + 1}`}
                    </span>
                    <Button
                      buttonType="icon-only"
                      size="sm"
                      onClick={() => handleStartEdit(speaker.speaker_id)}
                      aria-label="名前を編集"
                    >
                      <span className="material-symbols-outlined">edit</span>
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* セグメント一覧 */}
        <div className="diarize-modal__segments">
          <div className="diarize-modal__segments-title">発言内容</div>
          <div className="diarize-modal__segments-list">
            {segments.map((segment) => (
              <div key={segment.id} className="diarize-modal__segment">
                <div className="diarize-modal__segment-header">
                  <span
                    className="diarize-modal__speaker-badge"
                    style={{ backgroundColor: getSpeakerColor(segment.speaker_id) }}
                  >
                    {segment.speaker_id + 1}
                  </span>
                  <span className="diarize-modal__segment-speaker">
                    {getSpeakerDisplayName(segment.speaker_id)}
                  </span>
                  <span className="diarize-modal__segment-time">
                    {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                  </span>
                </div>
                <div className="diarize-modal__segment-text">
                  {segment.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .diarize-modal {
          width: min(95%, 60rem) !important;
          max-height: 90vh;
        }

        .diarize-modal__content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          max-height: 70vh;
          overflow: hidden;
        }

        .diarize-modal__speakers {
          flex-shrink: 0;
        }

        .diarize-modal__speakers-title,
        .diarize-modal__segments-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-gray600);
          margin-bottom: 0.75rem;
        }

        .diarize-modal__speakers-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .diarize-modal__speaker-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--color-gray50);
          border-radius: var(--radius-md);
          min-width: 150px;
        }

        .diarize-modal__speaker-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: var(--radius-full);
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .diarize-modal__speaker-display {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          flex: 1;
        }

        .diarize-modal__speaker-name {
          flex: 1;
          font-size: 0.875rem;
        }

        .diarize-modal__speaker-edit {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          flex: 1;
        }

        .diarize-modal__segments {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .diarize-modal__segments-list {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding-right: 0.5rem;
        }

        .diarize-modal__segment {
          padding: 0.75rem 1rem;
          background: var(--color-gray50);
          border-radius: var(--radius-md);
          border-left: 3px solid transparent;
        }

        .diarize-modal__segment-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .diarize-modal__segment-speaker {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-gray700);
        }

        .diarize-modal__segment-time {
          margin-left: auto;
          font-size: 0.75rem;
          color: var(--color-gray500);
          font-family: monospace;
        }

        .diarize-modal__segment-text {
          font-size: 0.875rem;
          line-height: 1.6;
          color: var(--color-gray800);
          white-space: pre-wrap;
        }

        /* スクロールバーのスタイル */
        .diarize-modal__segments-list::-webkit-scrollbar {
          width: 6px;
        }

        .diarize-modal__segments-list::-webkit-scrollbar-track {
          background: var(--color-gray100);
          border-radius: 3px;
        }

        .diarize-modal__segments-list::-webkit-scrollbar-thumb {
          background: var(--color-gray300);
          border-radius: 3px;
        }

        .diarize-modal__segments-list::-webkit-scrollbar-thumb:hover {
          background: var(--color-gray400);
        }
      `}</style>
    </Dialog>
  );
};

export default DiarizeModal;


